// Red del portal: que un corte de segundos NUNCA le enseñe un error crudo («TypeError: Failed to fetch») al cliente.
//  - `reintentar`: reintenta con espera creciente (≈0.5, 1, 2, 4 y 8 s, con jitter) ante fallas de red o
//    respuestas temporales (408, 429, 5xx). Los errores de negocio (permiso, validación) NO se reintentan.
//  - `crearFetchConReintentos`: el `fetch` que usa el cliente de Supabase del portal (rpc, storage y auth):
//    tiempo límite por petición (15 s, con AbortController) + `reintentar`.
//  - Un estado global (`useEstadoRed`) para el aviso discreto «Reconectando…» / «No pudimos conectar…».
import { useSyncExternalStore } from "react";

export const MENSAJE_SIN_CONEXION = "No pudimos conectar. Revisa tu internet; seguimos intentando.";
export const ESPERAS_MS = [500, 1000, 2000, 4000, 8000];
export const TIEMPO_LIMITE_MS = 15_000;
/** Cada cuánto se vuelve a intentar sola una vista o la cola cuando de plano no hubo conexión. */
export const REINTENTO_LENTO_MS = 20_000;

/** Falla de conexión ya traducida: el mensaje es para la persona, nunca «TypeError». */
export class ErrorDeRed extends Error {
  constructor(public causa?: unknown) {
    super(MENSAJE_SIN_CONEXION);
    this.name = "ErrorDeRed";
  }
}

const RE_RED =
  /failed to fetch|networkerror|network request failed|load failed|fetch failed|err_internet_disconnected|err_network|timed out|signal is aborted|the operation was aborted|tiempo agotado/i;

/** ¿Es una falla de conexión (y no un error de negocio)? Acepta errores de fetch, de Supabase y de auth. */
export function esErrorDeRed(e: unknown): boolean {
  if (!e) return false;
  if (e instanceof ErrorDeRed) return true;
  if (typeof e !== "object") return typeof e === "string" && RE_RED.test(e);
  const x = e as { name?: string; message?: string; status?: number; originalError?: unknown };
  if (x.name === "AbortError" || x.name === "TimeoutError" || x.name === "AuthRetryableFetchError") return true;
  if (x.name === "TypeError" && RE_RED.test(x.message ?? "")) return true;
  if (typeof x.status === "number" && esEstadoTemporal(x.status)) return true;
  if (x.originalError && esErrorDeRed(x.originalError)) return true;
  return RE_RED.test(x.message ?? "");
}

/** 408, 429 y 5xx: la petición puede salir bien si se repite. */
export const esEstadoTemporal = (s: number) => s === 408 || s === 429 || (s >= 500 && s <= 599);

// ---------------------------------------------------------------- estado global de la conexión
export type EstadoRed = {
  /** Peticiones esperando para reintentar ahora mismo. */
  reintentando: number;
  /** La última petición se rindió por falta de conexión (se limpia con la siguiente que salga bien). */
  sinConexion: boolean;
};
let estado: EstadoRed = { reintentando: 0, sinConexion: false };
const oyentes = new Set<() => void>();
function poner(cambio: Partial<EstadoRed>) {
  const nuevo = { ...estado, ...cambio };
  if (nuevo.reintentando === estado.reintentando && nuevo.sinConexion === estado.sinConexion) return;
  estado = nuevo;
  oyentes.forEach((f) => f());
}
export const leerEstadoRed = () => estado;
export function suscribirRed(f: () => void) {
  oyentes.add(f);
  return () => void oyentes.delete(f);
}
export const useEstadoRed = () => useSyncExternalStore(suscribirRed, leerEstadoRed, leerEstadoRed);
/** Solo pruebas. */
export const reiniciarEstadoRed = () => poner({ reintentando: 0, sinConexion: false });

/** Espera `ms`, pero se despierta antes si el navegador avisa que volvió la red. */
function dormir(ms: number): Promise<void> {
  const w = typeof window !== "undefined" ? window : null;
  return new Promise((listo) => {
    const fin = () => {
      clearTimeout(t);
      w?.removeEventListener("online", fin);
      listo();
    };
    const t = setTimeout(fin, ms);
    w?.addEventListener("online", fin);
  });
}

export type OpcionesReintento = {
  esperas?: number[];
  /** ¿Se reintenta este error? Por omisión: solo fallas de red. */
  reintentable?: (e: unknown) => boolean;
  /** Espera pedida por el servidor (Retry-After), en ms; se respeta con tope de 8 s. */
  esperaPedida?: (e: unknown) => number | null;
};

/**
 * Corre `intento` y, si falla por la red, lo repite con espera creciente y jitter. Si se agotan los intentos,
 * lanza el último error (y el aviso pasa a «No pudimos conectar…»).
 */
export async function reintentar<T>(intento: () => Promise<T>, op: OpcionesReintento = {}): Promise<T> {
  const esperas = op.esperas ?? ESPERAS_MS;
  const reintentable = op.reintentable ?? esErrorDeRed;
  let esperando = false;
  try {
    for (let i = 0; ; i++) {
      try {
        const r = await intento();
        poner({ sinConexion: false });
        return r;
      } catch (e) {
        if (!reintentable(e)) throw e;
        if (i >= esperas.length) {
          poner({ sinConexion: true });
          throw e;
        }
        if (!esperando) {
          esperando = true;
          poner({ reintentando: estado.reintentando + 1 });
        }
        const base = esperas[i];
        const pedida = op.esperaPedida?.(e);
        const ms = pedida != null ? Math.min(Math.max(pedida, base), 8000) : base * (0.75 + Math.random() * 0.5);
        await dormir(ms);
      }
    }
  } finally {
    if (esperando) poner({ reintentando: Math.max(0, estado.reintentando - 1) });
  }
}

/** Respuesta 408/429/5xx: se lanza dentro del ciclo para reintentar; si se agotan, se entrega tal cual. */
class RespuestaTemporal extends Error {
  constructor(public respuesta: Response) {
    super(`HTTP ${respuesta.status}`);
    this.name = "RespuestaTemporal";
  }
}

export type OpcionesFetch = { fetch?: typeof fetch; esperas?: number[]; tiempoLimite?: number };

/**
 * `fetch` con tiempo límite y reintentos, para `createClient(..., { global: { fetch } })`.
 * Respeta el `signal` de quien llama: si él aborta, no se reintenta.
 */
export function crearFetchConReintentos(o: OpcionesFetch = {}): typeof fetch {
  const tiempoLimite = o.tiempoLimite ?? TIEMPO_LIMITE_MS;
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const base = o.fetch ?? globalThis.fetch.bind(globalThis);
    const externo = init?.signal ?? (input instanceof Request ? input.signal : null);
    try {
      return await reintentar(
        async () => {
          if (externo?.aborted) throw externo.reason ?? new DOMException("Cancelada", "AbortError");
          const ctrl = new AbortController();
          const t = setTimeout(() => ctrl.abort(new DOMException("Tiempo agotado", "TimeoutError")), tiempoLimite);
          const cancelar = () => ctrl.abort(externo?.reason);
          externo?.addEventListener("abort", cancelar, { once: true });
          try {
            // Un Request se clona en cada intento (su cuerpo solo se puede leer una vez).
            const req = input instanceof Request ? input.clone() : input;
            const r = await base(req, { ...init, signal: ctrl.signal });
            if (esEstadoTemporal(r.status)) throw new RespuestaTemporal(r);
            return r;
          } finally {
            clearTimeout(t);
            externo?.removeEventListener("abort", cancelar);
          }
        },
        {
          esperas: o.esperas,
          reintentable: (e) => !externo?.aborted && (e instanceof RespuestaTemporal || esErrorDeRed(e)),
          esperaPedida: (e) => {
            if (!(e instanceof RespuestaTemporal)) return null;
            const s = Number(e.respuesta.headers.get("retry-after"));
            return Number.isFinite(s) && s > 0 ? s * 1000 : null;
          },
        },
      );
    } catch (e) {
      if (e instanceof RespuestaTemporal) return e.respuesta; // que el cliente de Supabase lo lea como siempre
      throw e;
    }
  };
}

/** Vuelve a correr `f` al regresar la red, al volver el foco a la pestaña y cada `cadaMs`. Devuelve cómo apagarlo. */
export function alReconectar(f: () => void, cadaMs = REINTENTO_LENTO_MS): () => void {
  const visible = () => {
    if (document.visibilityState === "visible") f();
  };
  window.addEventListener("online", f);
  window.addEventListener("focus", f);
  document.addEventListener("visibilitychange", visible);
  const t = setInterval(f, cadaMs);
  return () => {
    window.removeEventListener("online", f);
    window.removeEventListener("focus", f);
    document.removeEventListener("visibilitychange", visible);
    clearInterval(t);
  };
}
